import AXIOS_API from "../helpers/axios";
import { CreateScriptCategoryDto, GetScriptsResponse, ScriptCategoryDto, ScriptDetailDto, UpdateScriptCategoryDto } from "../helpers/types";
import { socketService } from "./socket.service";

class ChatScriptService {
  public async getCategories(creatorId: string): Promise<ScriptCategoryDto[]> {
    const url = `/script-category/${encodeURIComponent(creatorId)}`;
    const res = await AXIOS_API.get(url, { withCredentials: true });
    const payload = res?.data;
    if (Array.isArray(payload)) return payload as ScriptCategoryDto[];
    return (payload?.data as ScriptCategoryDto[]) || [];
  }

  public async createCategory(payload: CreateScriptCategoryDto): Promise<ScriptCategoryDto> {
    const res = await AXIOS_API.post("/script-category", payload, { withCredentials: true });
    const body = res?.data;
    return (body?.data as ScriptCategoryDto) ?? (body as ScriptCategoryDto);
  }

  public async updateCategory(id: string, payload: UpdateScriptCategoryDto): Promise<ScriptCategoryDto> {
    const res = await AXIOS_API.put(`/script-category/${encodeURIComponent(id)}`, payload, { withCredentials: true });
    const body = res?.data;
    return (body?.data as ScriptCategoryDto) ?? (body as ScriptCategoryDto);
  }

  public async deleteCategory(id: string): Promise<boolean> {
    const res = await AXIOS_API.delete(`/script-category/${encodeURIComponent(id)}`, { withCredentials: true });
    const body = res?.data;
    if (typeof body === "boolean") return body;
    return !!(body?.data ?? body?.success ?? true);
  }

  public async createScript(payload: Record<string, any>): Promise<any> {
    const res = await AXIOS_API.post("/script-message", payload, { withCredentials: true });
    const body = res?.data;
    return body?.data ?? body;
  }

  public createScriptV2 = (data: FormData, setProgress: Function) => {
    return new Promise<any>((resolve, reject) => {
      AXIOS_API.post(`/script-message`, data, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        onUploadProgress: (event) => {
          let progress = Math.round((100 * event.loaded) / event.total!);
          progress = Math.round(progress / 2);
          if (progress <= 50) {
            setProgress(progress);
          }
        },
      })
        .then((response) => {
          if (response.status === 200) {
            if (response?.data?.data) {
              resolve(response.data.data);
            } else if (response?.data?.message) {
              resolve(response.data);
            }
          } else {
            reject("Something went wrong.");
          }
        })
        .catch((error) => {
          if (error?.response?.data?.message) reject(error.response.data.message);
          else reject("Something went wrong.");
        });
    });
  };

  public async getScripts(params: { filterOrCategoryId: string; creatorId: string; page?: number }): Promise<GetScriptsResponse> {
    const { filterOrCategoryId, creatorId, page = 1 } = params;
    const url = `/script-message?filter=${encodeURIComponent(filterOrCategoryId)}&creatorId=${encodeURIComponent(creatorId)}&page=${page}`;
    const res = await AXIOS_API.get(url, { withCredentials: true });
    const body = res?.data;
    const payload = body?.data || body;
    return {
      data: payload?.data || [],
      total: payload?.total ?? 0,
      totalPages: payload?.totalPages ?? 0,
    };
  }

  public async searchScripts(params: { text: string; creatorId: string; page?: number }): Promise<GetScriptsResponse> {
    const { text, creatorId, page = 1 } = params;
    const url = `/script-message/search?text=${encodeURIComponent(text)}&creatorId=${encodeURIComponent(creatorId)}&page=${page}`;
    const res = await AXIOS_API.get(url, { withCredentials: true });
    const body = res?.data;
    const payload = body?.data || body;
    return {
      data: payload?.data || [],
      total: payload?.total ?? 0,
      totalPages: payload?.totalPages ?? 0,
    };
  }

  public async getScriptById(id: string): Promise<ScriptDetailDto> {
    const url = `/script-message/${encodeURIComponent(id)}`;
    const res = await AXIOS_API.get(url, { withCredentials: true });
    const body = res?.data;
    return (body?.data as ScriptDetailDto) ?? (body as ScriptDetailDto);
  }

  public async deleteScript(id: string): Promise<boolean> {
    const url = `/script-message/${encodeURIComponent(id)}`;
    const res = await AXIOS_API.delete(url, { withCredentials: true });
    const body = res?.data;
    if (typeof body === "boolean") return body;
    return !!(body?.data ?? body?.success ?? false);
  }

  public async updateScript(id: string, payload: FormData | Record<string, any>): Promise<any> {
    const url = `/script-message/${encodeURIComponent(id)}`;
    const res = await AXIOS_API.patch(url, payload, { withCredentials: true });
    const body = res?.data;
    return body?.data ?? body;
  }

  public updateScriptV2 = (id: string, data: FormData, setProgress: Function) => {
    return new Promise<any>((resolve, reject) => {
      AXIOS_API.patch(`/script-message/${encodeURIComponent(id)}`, data, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        onUploadProgress: (event) => {
          let progress = Math.round((100 * event.loaded) / event.total!);
          progress = Math.round(progress / 2);
          if (progress <= 50) {
            setProgress(progress);
          }
        },
      })
        .then((response) => {
          if (response.status === 200) {
            if (response?.data?.data) {
              resolve(response.data.data);
            } else if (response?.data?.message) {
              resolve(response.data);
            }
          } else {
            reject("Something went wrong.");
          }
        })
        .catch((error) => {
          if (error?.response?.data?.message) reject(error.response.data.message);
          else reject("Something went wrong.");
        });
    });
  };

  public async reorderScripts(params: { creatorId: string; start: number; end: number; categoryId: string }): Promise<any> {
    const { creatorId, start, end, categoryId } = params;
    const query = new URLSearchParams({ start: String(start), end: String(end), categoryId }).toString();
    const url = `/script-message/reorder/${encodeURIComponent(creatorId)}?${query}`;
    const res = await AXIOS_API.put(url, {}, { withCredentials: true });
    return res?.data;
  }

  public async sendScript(payload: any): Promise<any> {
    const res = await AXIOS_API.post("/script-message/send", payload, {
      headers: {
        ...socketService.getSocketIdForHeaders(),
      },
      withCredentials: true,
    });
    const body = res?.data;
    return body?.data ?? body;
  }
}

export const chatScriptService = new ChatScriptService();
